<template>
  <Modal :footer-hide="true" :mask-closable="true" v-model="show" :title="'导出' + (params.title || 'Excel')" :width="1000" @on-cancel="clearFrom">
    <div class="QHexcelDownload QHviewUiStyle">
      <table class="QHtable">
        <tbody>
          <tr>
            <td width="100px"><b>导出条数</b></td>
            <td>{{ totalCount || 0 }}条</td>
          </tr>
          <tr>
            <td><b>Excel名</b></td>
            <td>{{ filename }}</td>
          </tr>
          <tr>
            <td><b>需导出列</b></td>
            <td>
              <Checkbox v-for="(item, index) in params.columns" :key="item.name" :value="item.checked" @on-change="(checked) => handleCheck(index, checked)">
                {{ item.title }}
              </Checkbox>
            </td>
          </tr>
          <tr v-if="progressStatus != 'normal'">
            <td><b>导出进度</b></td>
            <td class="QHprogress">
              <!-- 动态绑定进度值 -->
              <p class="p1" v-if="progressStatus == 'active'">
                <Progress :percent="progressValue" status="active" />
                <span style="margin-top: 5px; font-size: 12px">{{ msg }}</span>
              </p>
              <p class="p2 flex-center-y" v-if="progressStatus == 'wrong'">
                <span class="s1"><Icon type="md-close-circle" /></span>
                <span class="s2">{{ msg }}</span>
              </p>
              <p class="p3 flex-center-y" v-if="progressStatus == 'success'">
                <span class="s1"><Icon type="md-cloud-done" /></span>
                <!-- 显示实际导出条数 -->
                <span class="s2">成功导出{{ exportedCount }}条数据，请在浏览器下载文件夹中查看</span>
              </p>
            </td>
          </tr>
          <tr v-if="progressStatus == 'normal'">
            <td colspan="10"><Button @click="goExport()" size="large" type="primary" long icon="md-cloud-download">导出Excel</Button></td>
          </tr>
          <tr v-if="progressStatus == 'active'">
            <td colspan="10">
              <Button @click="stopExport()" size="large" type="error" long icon="ios-close-circle">停止导出</Button>
            </td>
          </tr>
          <tr v-if="progressStatus == 'wrong'">
            <td colspan="10">
              <Button @click="reExport()" size="large" type="error" long icon="ios-close-circle">重新导出</Button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Modal>
</template>

<script>
import ExcelExporter from "./excelExport";

export default {
  data() {
    return {
      // 组件状态
      show: false,
      progressStatus: "normal", // normal, active, wrong, success
      msg: "",
      filename: "",
      progressValue: 0,
      exportedCount: 0,
      totalCount: 0,

      // 传入参数
      params: {
        title: "",
        api: null, // 函数类型
        searchMap: {},
        columns: [],
      },

      // 导出时间
      time: "",

      // 文件导出器实例
      exporter: null,

      // 分页参数
      pageNo: 1,
      pageSize: 1000, // 增加每页大小提高性能

      // 取消控制
      cancelToken: { cancel: false },
    };
  },

  beforeDestroy() {
    this.clearExporter();
  },

  methods: {
    // +----------------------------------------------------------------------
    // | 导出Excel主方法
    // +----------------------------------------------------------------------
    async goExport() {
      // 确保导出器已初始化
      if (!this.exporter) {
        this.exporter = new ExcelExporter();
      }

      this.progressStatus = "active";
      this.reParameter();

      try {
        //0.取出选中的列
        let checkedColumns = [];
        for (let i = 0; i < this.params.columns.length; i++) {
          if (this.params.columns[i].checked) {
            checkedColumns.push(this.params.columns[i]);
          }
        }
        if (checkedColumns.length < 3) {
          throw new Error("请选择要导出的列,至少3列");
        }

        // 1. 设置标题
        this.exporter.setTitle(this.params.title + this.totalCount + "条" + "(导出时间" + this.time + ")", checkedColumns.length);
        this.updateProgress(5, "设置标题完成");

        // 2. 设置表头
        this.exporter.setHeaders(checkedColumns);
        this.updateProgress(10, "设置表头完成");

        // 3. 分页导出数据
        let lastUpdate = 0; // 用于进度节流
        while (this.exportedCount < this.totalCount && !this.cancelToken.cancel) {
          const data = await this.getPageData();

          // 检查取消状态
          if (this.cancelToken.cancel) break;

          if (!data || data.length === 0) break;

          // 添加数据行
          this.exporter.addDataRows(data, () => {
            // 检查取消状态
            if (this.cancelToken.cancel) return;

            this.exportedCount += data.length;

            // 进度节流：每1000条或每秒更新一次进度
            if (this.exportedCount % 1000 === 0 || Date.now() - lastUpdate > 1000) {
              const progress = Math.round((this.exportedCount / this.totalCount) * 80) + 15;
              this.updateProgress(progress, `已导出 ${this.exportedCount} 条`);
              lastUpdate = Date.now();
            }
          });

          this.pageNo++;

          // 检查取消状态
          if (this.cancelToken.cancel) break;

          // 如果数据不足一页，说明已导出所有数据
          if (data.length < this.pageSize) break;

          // 每10页添加延迟避免阻塞UI
          if (this.pageNo % 10 === 0) {
            await new Promise((resolve) => setTimeout(resolve, 50));
          }
        }

        // 4. 检查是否取消
        if (this.cancelToken.cancel) {
          throw new Error("导出已取消");
        }

        // 5. 导出文件
        this.updateProgress(95, "生成Excel文件");
        const result = await this.exporter.export(this.filename);

        if (result) {
          this.updateProgress(100, `成功导出${this.exportedCount}条数据，请在浏览器下载文件夹中查看`, "success");
        } else {
          throw new Error("Excel文件导出失败");
        }
      } catch (error) {
        this.handleError(error);
      }
    },

    // +----------------------------------------------------------------------
    // | 停止导出
    // +----------------------------------------------------------------------
    stopExport() {
      this.cancelToken.cancel = true;
      this.progressStatus = "wrong";
      this.msg = "导出已取消";

      // 只清除导出器状态，不置为null
      if (this.exporter) {
        this.exporter.cancelAndClear();
      }
    },

    // +----------------------------------------------------------------------
    // | 重新导出
    // +----------------------------------------------------------------------
    reExport() {
      this.cancelToken.cancel = false;
      this.progressStatus = "active";

      // 重置导出器
      this.clearExporter();
      this.exporter = new ExcelExporter();

      this.reParameter();
      this.goExport();
    },

    // +----------------------------------------------------------------------
    // | 获取分页数据
    // +----------------------------------------------------------------------
    async getPageData() {
      try {
        // 检查取消状态
        if (this.cancelToken.cancel) return [];

        const parameter = {
          ...this.params.searchMap,
          pageNo: this.pageNo,
          pageSize: this.pageSize,
        };
        const response = await this.params.api(parameter);
        if (response.code !== 0) {
          throw new Error(response.msg || "API返回错误");
        }
        return this.formatData(response.data);
      } catch (error) {
        throw new Error("获取数据失败: " + error.message);
      }
    },

    // +----------------------------------------------------------------------
    // | 格式化数据
    // +----------------------------------------------------------------------
    formatData(data) {
      //格式化配置文件
      const columns = this.$copyObj(this.params.columns);
      const list = this.$copyObj(data) || [];

      //检查输入数据
      if (this.$isblank(list)) {
        return [];
      }
      if (this.$isblank(columns)) {
        return [];
      }

      //收集需要保留的字段
      var keepKeys = [];
      var length = columns.length;
      for (var i = 0; i < length; i++) {
        keepKeys.push(columns[i]["key"]);
      }

      //处理数据
      var result = [];
      for (var j = 0; j < list.length; j++) {
        var newRow = {};
        for (var k = 0; k < keepKeys.length; k++) {
          var key = keepKeys[k];
          newRow[key] = list[j][key];
        }
        result.push(newRow);
      }

      //按列配置进一步格式化
      for (var i = 0; i < length; i++) {
        var columnsObj = columns[i];
        var key = columnsObj["key"];
        var fmtType = columnsObj["fmtType"];
        var fmtValue = columnsObj["fmtValue"];

        //处理计量单位
        if (fmtType === "unit" && fmtValue) {
          for (var j = 0; j < result.length; j++) {
            var cell = result[j][key];
            if (!this.$isblank(cell) || cell == 0) {
              result[j][key] = cell + fmtValue;
            }
          }
        }

        //处理时间格式
        if (fmtType === "date" && fmtValue) {
          for (var j = 0; j < result.length; j++) {
            var cell = result[j][key];
            if (!this.$isblank(cell)) {
              result[j][key] = this.$timeFmt(cell, fmtValue);
            }
          }
        }

        //处理转义数据
        if (fmtType === "RwbConfig" && fmtValue) {
          for (var j = 0; j < result.length; j++) {
            var cell = result[j][key];
            if (!this.$isblank(cell) || cell == 0) {
              result[j][key] = this.$CVALUE(fmtValue, cell) || cell;
            }
          }
        }

        //处理ID加密
        if (fmtType === "decryptToId") {
          for (var j = 0; j < result.length; j++) {
            var cell = result[j][key];
            if (!this.$isblank(cell)) {
              result[j][key] = this.$decryptToId(cell);
            }
          }
        }

        //距离处理
        if (fmtType === "mFmt") {
          for (var j = 0; j < result.length; j++) {
            var cell = result[j][key];
            if (!this.$isblank(cell)) {
              result[j][key] = this.$mFmt(cell);
            }
          }
        }
      }

      //返回与columns完全对应的数据
      return result;
    },

    // +----------------------------------------------------------------------
    // | 弹出导出弹出框
    // +----------------------------------------------------------------------
    async hiPop(params) {
      try {
        // 清空参数
        this.clearFrom();

        //导出时间
        this.time = this.$timeFmt(this.$getNowUnix(), "YYYY年MM月DD日HH时mm分ss秒");

        // 验证参数是否存在
        if (!params) {
          this.$Message.error("缺少导出参数");
          return false;
        }

        // 验证必要参数
        this.params = [];
        this.params = params;

        // 1. 验证标题
        if (!this.params.title || typeof this.params.title !== "string") {
          this.$Message.error("缺少有效的导出标题");
          return false;
        }

        // 2. 验证API函数
        if (!this.params.api || typeof this.params.api !== "function") {
          this.$Message.error("缺少有效的API函数");
          return false;
        }

        // 3. 验证列配置
        if (!Array.isArray(this.params.columns) || this.params.columns.length === 0) {
          this.$Message.error("缺少有效的列配置");
          return false;
        }

        // 4. 验证列配置格式
        const validColumns = this.params.columns.every((col) => {
          return col.title && typeof col.title === "string" && col.key && typeof col.key === "string";
        });
        if (!validColumns) {
          this.$Message.error("列配置格式不正确，每列必须包含title和key属性");
          return false;
        }

        // 5. 为每一个列增加临时选中项
        this.params.columns.forEach((col) => {
          col.checked = true;
        });

        // 查总数据量
        this.$loading(true);
        const response = await this.params.api({
          ...this.params.searchMap,
          pageNo: 1,
          pageSize: this.pageSize,
          countOnly: true,
        });
        this.$loading(false);

        // 验证API返回结果
        if (response.code !== 0) {
          this.$Message.error(response.msg || "获取总数失败");
          return false;
        }

        // 设置总数据量
        this.totalCount = response.data || 0;
        if (this.totalCount == 0) {
          this.$Message.error("暂无可导出数据");
          return false;
        }

        // 配置文件名
        const filenameTime = this.totalCount + "条(导出时间" + this.time + ")" + ".xlsx";
        this.filename = this.params.title + filenameTime;

        // 创建或重置导出器
        this.clearExporter(); // 先清理旧的导出器
        this.exporter = new ExcelExporter(); // 创建新的导出器

        // 显示导出对话框
        this.show = true;
        return true;
      } catch (error) {
        console.error("初始化导出失败:", error);
        this.$Message.error(error.message || "导出初始化失败");
        return false;
      }
    },

    // +----------------------------------------------------------------------
    // | 执行导出参数前重构条件
    // +----------------------------------------------------------------------
    reParameter() {
      this.pageNo = 1;
      this.progressValue = 0;
      this.exportedCount = 0;
      this.msg = "";
      this.cancelToken.cancel = false;

      // 重置导出器状态（不创建新实例）
      if (this.exporter) {
        this.exporter.clear();
      }
    },

    // +----------------------------------------------------------------------
    // | 停止导出清理并退出
    // +----------------------------------------------------------------------
    clearFrom() {
      this.pageNo = 1;
      this.show = false;
      this.progressStatus = "normal";
      this.msg = "";
      this.filename = "";
      this.progressValue = 0;
      this.exportedCount = 0;
      this.totalCount = 0;
      this.params = [];

      // 清理导出器但不置为null
      if (this.exporter) {
        this.exporter.clear();
      }
    },

    // +----------------------------------------------------------------------
    // | 清理导出器
    // +----------------------------------------------------------------------
    clearExporter() {
      if (this.exporter) {
        this.exporter.clear();
        this.exporter = null; // 设置为null以便重新创建
      }
    },

    // +----------------------------------------------------------------------
    // | 更新进度
    // +----------------------------------------------------------------------
    updateProgress(value, message, status = null) {
      this.progressValue = value;
      this.msg = message;
      if (status) this.progressStatus = status;
    },
    // +----------------------------------------------------------------------
    // | 导出列选择
    // +----------------------------------------------------------------------
    handleCheck(index, checked) {
      this.params['columns'][index].checked = checked;
    },

    // +----------------------------------------------------------------------
    // | 错误处理
    // +----------------------------------------------------------------------
    handleError(error) {
      console.error("导出失败:", error);
      this.msg = error.message || "导出过程中发生错误";
      this.progressStatus = "wrong";
      this.clearExporter();
    },
  },
};
</script>

<style>
.QHexcelDownload .QHprogress P {
  line-height: 20px;
}
.QHexcelDownload .QHprogress .s1 {
  margin-right: 5px;
  font-size: 20px;
}
.QHexcelDownload .QHprogress .s2 {
  font-size: 14px;
}
.QHexcelDownload .QHprogress .p2 {
  color: #f00;
}
.QHexcelDownload .QHprogress .p3 {
  color: #06a51e;
}
.QHexcelDownload .ivu-btn {
  font-size: 15px;
  font-weight: 700;
}
.QHexcelDownload .ivu-btn .ivu-icon {
  font-weight: normal;
  font-size: 20px;
}
</style>
